import React, { useState, useEffect, useCallback, useMemo } from "react";
import DerivedDataChart from "./DerivedDataChart";
import { useDerivedData, usePnLData } from "../../hooks/useMarketData";
import { fetchDerivedDataLatest } from "../../services/derivedData";
import api from "../../services/api";
import "./CompactDerivedChart.css";

const CompactDerivedChart = ({ selectedIndex }) => {
  const [historicalData, setHistoricalData] = useState(null);
  const [pnlHistoricalData, setPnlHistoricalData] = useState(null);
  const [loading, setLoading] = useState(false);

  // WebSocket hooks for real-time updates
  const { data: wsData } = useDerivedData(selectedIndex, !!selectedIndex);
  const { data: wsPnLData } = usePnLData();

  // Fetch historical data from 9:15 AM (1-minute candles)
  const loadHistoricalData = useCallback(async () => {
    if (!selectedIndex) return;

    setLoading(true);
    try {
      // Calculate 9:15 AM today as start time
      const now = new Date();
      const marketOpen = new Date(now);
      marketOpen.setHours(9, 15, 0, 0);
      const startTimestamp = Math.floor(marketOpen.getTime() / 1000);

      const response = await fetchDerivedDataLatest(selectedIndex, '1m', startTimestamp);
      if (response.success && response.data?.candles) {
        const chartData = response.data.candles.map(candle => ({
          timestamp: candle.timestamp,
          index_price: candle.index_price,
          synthetic_future: candle.synthetic_future,
          straddle_price: candle.straddle_price,
          atm_strike: candle.atm_strike,
        }));
        setHistoricalData(chartData);
      }
    } catch (err) {
      console.error("Error fetching historical data:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedIndex]);

  // Fetch PnL historical data from 9:15 AM
  const loadPnLHistoricalData = useCallback(async () => {
    try {
      const now = new Date();
      const marketOpen = new Date(now);
      marketOpen.setHours(9, 15, 0, 0);
      const startTimestamp = Math.floor(marketOpen.getTime() / 1000);
      const endTimestamp = Math.floor(now.getTime() / 1000);

      const response = await api.get(
        `/pnl/history/total?start=${startTimestamp}&end=${endTimestamp}&limit=25000&granularity=1m`
      );

      if (response.data?.data) {
        const pnlData = response.data.data.map(point => ({
          timestamp: point.timestamp,
          value: point.value,
        }));
        setPnlHistoricalData(pnlData);
      }
    } catch (err) {
      console.error("Error fetching PnL history:", err);
    }
  }, []);

  // Calculate derived metrics for trading decisions
  const metrics = useMemo(() => {
    if (!wsData) return null;
    const { spot_price, synthetic_future, straddle_price } = wsData;
    if (!spot_price || !straddle_price) return null;

    const expectedMove = (straddle_price / spot_price) * 100;
    const basis = synthetic_future - spot_price;
    const skew = straddle_price > 0 ? basis / straddle_price : 0;
    const lowerBound = Math.round(spot_price - straddle_price);
    const upperBound = Math.round(spot_price + straddle_price);

    let mode = 'NORMAL';
    if (skew < -0.3) mode = 'FEAR';
    else if (skew > 0.3) mode = 'FOMO';

    return { expectedMove, basis, skew, mode, lowerBound, upperBound };
  }, [wsData]);

  // Load historical data on index change
  useEffect(() => {
    setHistoricalData(null);
    loadHistoricalData();
    loadPnLHistoricalData();
  }, [selectedIndex, loadHistoricalData, loadPnLHistoricalData]);

  return (
    <div className="compact-chart-container">
      {metrics && (
        <div className="compact-metrics-bar">
          <span className="metric" title="Expected Move: Market's expected price range by expiry. Calculated as (Straddle/Spot)%. Strikes outside this range are safer for selling.">
            <label>EM:</label>
            <span className="metric-val">{metrics.expectedMove.toFixed(2)}% ({metrics.lowerBound}-{metrics.upperBound})</span>
          </span>
          <span className="metric" title="Basis: Synthetic Future - Spot. Positive = Calls expensive (bullish bias). Negative = Puts expensive (bearish bias).">
            <label>Basis:</label>
            <span className={`metric-val ${metrics.basis >= 0 ? 'positive' : 'negative'}`}>
              {metrics.basis >= 0 ? '+' : ''}{metrics.basis.toFixed(1)}
            </span>
          </span>
          <span className="metric" title="Skew: Basis/Straddle. Range -1 to +1. Near 0 = balanced. Extreme values tend to mean-revert.">
            <label>Skew:</label>
            <span className={`metric-val ${metrics.skew >= 0 ? 'positive' : 'negative'}`}>
              {metrics.skew >= 0 ? '+' : ''}{metrics.skew.toFixed(2)}
            </span>
          </span>
          <span className={`metric mode-${metrics.mode.toLowerCase()}`} title="Mode: FEAR (skew<-0.3, puts expensive), NORMAL (balanced, theta works), FOMO (skew>0.3, calls expensive)">
            <label>Mode:</label>
            <span className="metric-val">{metrics.mode}</span>
          </span>
        </div>
      )}
      {loading && <div className="compact-chart-loading">Loading...</div>}
      <DerivedDataChart
        indexName={selectedIndex}
        realtimeCandle={wsData}
        historicalData={historicalData}
        timeWindowMinutes={0}
        pnlData={pnlHistoricalData}
        realtimePnL={wsPnLData}
      />
    </div>
  );
};

export default CompactDerivedChart;
